import { createSlice } from "@reduxjs/toolkit";
import {
  deleteCollectionBuilder,
  generateCollectionBuilder,
  getCategoriesBuilder,
  getCollectionsBuilder,
  getExploreCollectionsBuilder,
  getUserFavoritesBuilder,
  toggleToFavoriteBuilder,
  getTrainModelsBuilder,
  getTrainModelBuilder,
  createTrainModelBuilder,
  updateTrainModelBuilder,
  deleteTrainModelBuilder,
  getTopCreatorsBuilder,
  regenerateCollectionBuilder,
  rateModelBuilder,
  getDefaultGalleryBuilder,
  selectCoverForTrainModelBuilder,
  submitTrainModelForReviewBuilder,
  getCollectionFoldersBuilder,
  createCollectionFolderBuilder,
  putCollectionToFolderBuilder,
  deleteCollectionFolderBuilder,
} from "./builder";
import { LOGOUT } from "../../../utils/constants";

const initialState = {
  categories: {
    data: [],
    loading: false,
    error: null,
  },

  generateCollection: {
    data: [],
    loading: false,
    error: null,
  },

  collections: {
    data: null,
    total: 0,
    perPage: 10,
    page: 0,
    maxReached: false,
    loading: false,
    error: null,
    hasDataBeenFetched: false,
  },

  gallery: {
    data: null,
  },

  favoriteCollections: {
    data: null,
    total: 0,
    perPage: 10,
    page: 0,
    maxReached: false,
    loading: false,
    error: null,
    hasDataBeenFetched: false,
  },
  exploreCollections: {
    data: null,
    maxReached: false,
    total: 0,
    perPage: 10,
    page: 0,
    loading: false,
    error: null,
    hasDataBeenFetched: false,
    filter: null,
  },
  selectedCollection: null,
  collectionFolders: {
    data: [],
    loading: false,
  },
  trainModels: {
    data: [],
    loading: false,
    error: null,
    hasDataBeenFetched: false,
  },

  selectedTrainModel: {
    data: null,
    loading: true,
    error: null,
    trainModelId: null,
  },

  createTrainModel: {
    loading: false,
    error: null,
  },

  updateTrainModel: {
    loading: false,
    error: null,
  },

  topCreators: [],

  toggleToFavoritePending: false,
  deleteCollectionPending: false,
  deleteTrainModelPending: false,
  selectCoverForTrainModelPending: false,
  submitTrainModelForReviewPending: false,
  regenerateCollectionPending: false,
  getTopCreatorsPending: true,
  rateModelPending: false,
  getDefaultGalleryPending: false,
  createFolderPending: false,
  deleteFolderPending: false,
  putCollectionToFolderPending: false,
};

export const modelSlice = createSlice({
  name: "modelInfo",
  initialState,
  reducers: {
    clearCollections: (state) => {
      state.collections.data = [];
      state.collections.page = 0;
      state.collections.maxReached = false;
      state.collections.loading = false;
      state.collections.error = null;
      state.collections.hasDataBeenFetched = false;
    },
    clearExploreCollections: (state) => {
      state.exploreCollections.data = null;
      state.exploreCollections.page = 0;
      state.exploreCollections.maxReached = false;
      state.exploreCollections.loading = false;
      state.exploreCollections.error = null;
      state.exploreCollections.hasDataBeenFetched = false;
    },
    // Dodajte reset akcije za kada se menja kategorija/tab
    resetCollectionsPagination: (state) => {
      state.collections.data = [];
      state.collections.page = 0;
      state.collections.maxReached = false;
    },
    resetExploreCollectionsPagination: (state) => {
      state.exploreCollections.data = [];
      state.exploreCollections.page = 0;
      state.exploreCollections.maxReached = false;
    },

    setSelectedCollection: (state, { payload }) => {
      state.selectedCollection = payload;
    },
    clearTrainModels: (state) => {
      state.trainModels.data = [];
      state.trainModels.loading = false;
      state.trainModels.error = null;
      state.trainModels.hasDataBeenFetched = false;
    },
    setSelectedTrainModel: (state, { payload }) => {
      state.selectedTrainModel.data = payload;
    },
    clearSelectedTrainModel: (state) => {
      state.selectedTrainModel.data = null;
      state.selectedTrainModel.loading = false;
      state.selectedTrainModel.error = null;
    },
    setSelectedExploreCollectionFilter: (state, { payload }) => {
      state.exploreCollections.filter = payload;
    },
  },
  extraReducers: (builder) => {
    getCategoriesBuilder(builder);
    getCollectionsBuilder(builder);
    getExploreCollectionsBuilder(builder);
    generateCollectionBuilder(builder);
    toggleToFavoriteBuilder(builder);
    getUserFavoritesBuilder(builder);
    deleteCollectionBuilder(builder);
    getTrainModelsBuilder(builder);
    getTrainModelBuilder(builder);
    createTrainModelBuilder(builder);
    updateTrainModelBuilder(builder);
    deleteTrainModelBuilder(builder);
    getTopCreatorsBuilder(builder);
    rateModelBuilder(builder);
    regenerateCollectionBuilder(builder);
    getDefaultGalleryBuilder(builder);
    selectCoverForTrainModelBuilder(builder);
    submitTrainModelForReviewBuilder(builder);
    getCollectionFoldersBuilder(builder);
    createCollectionFolderBuilder(builder);
    putCollectionToFolderBuilder(builder);
    deleteCollectionFolderBuilder(builder);
    builder.addCase(LOGOUT, (state) => ({
      ...initialState,
      categories: state.categories,
      exploreCollections: state.exploreCollections,
      loggedIn: false,
    }));
  },
});

export const {
  clearCollections,
  clearExploreCollections,
  resetCollectionsPagination,
  resetExploreCollectionsPagination,
  setSelectedCollection,
  clearTrainModels,
  setSelectedTrainModel,
  clearSelectedTrainModel,
  setSelectedExploreCollectionFilter,
} = modelSlice.actions;

export const selectCategories = (state) => state.modelInfo.categories;
export const selectGenerateCollection = (state) =>
  state.modelInfo.generateCollection;
export const selectCollections = (state) => state.modelInfo.collections;
export const selectFavorites = (state) => state.modelInfo.favoriteCollections;

export const selectFavoritesPage = (state) =>
  state.modelInfo.favoriteCollections.page;

export const selectToggleToFavoriteLoading = (state) =>
  state.modelInfo.toggleToFavoritePending;
export const selectExploreCollections = (state) =>
  state.modelInfo.exploreCollections;
export const selectExploreCollectionsFilter = (state) =>
  state.modelInfo.exploreCollections.filter;
export const selectExploreCollectionsPage = (state) =>
  state.modelInfo.exploreCollections.page;

export const selectSelectedCollection = (state) =>
  state.modelInfo.selectedCollection;

export const selectDeleteCollectionPending = (state) =>
  state.modelInfo.deleteCollectionPending;
export const selectRateModelPending = (state) =>
  state.modelInfo.rateModelPending;

export const selectTrainModels = (state) => state.modelInfo.trainModels;
export const selectSelectedTrainModel = (state) =>
  state.modelInfo.selectedTrainModel;
export const selectCreateTrainModelLoading = (state) =>
  state.modelInfo.createTrainModel.loading;
export const selectUpdateTrainModelLoading = (state) =>
  state.modelInfo.updateTrainModel.loading;

export const selectDeleteTrainModelPending = (state) =>
  state.modelInfo.deleteTrainModelPending;

export const selectTopCreators = (state) => state.modelInfo.topCreators;
export const selectGetTopCreatorsPending = (state) =>
  state.modelInfo.getTopCreatorsPending;

export const selectRegenerateCollectionPending = (state) =>
  state.modelInfo.regenerateCollectionPending;

export const selectGalleryData = (state) => state.modelInfo.gallery.data;
export const selectGalleryDataPending = (state) =>
  state.modelInfo.getDefaultGalleryPending;

export const selectCoverForTrainModePending = (state) =>
  state.modelInfo.selectCoverForTrainModelPending;

export const selectSubmitTrainModelForReviewPending = (state) =>
  state.modelInfo.submitTrainModelForReviewPending;

export const selectCollectionFolders = (state) =>
  state.modelInfo.collectionFolders;

export const selectCreateFolderPending = (state) =>
  state.modelInfo.createFolderPending;

export const selectDeleteFolderPending = (state) =>
  state.modelInfo.deleteFolderPending;

export const selectPutCollectionFolderPending = (state) =>
  state.modelInfo.putCollectionToFolderPending;

export default modelSlice.reducer;
